import {
	createRouter,
	createWebHistory,
} from 'vue-router';
import routes from './routes';

import { useNetworkStore } from '/src/store/stores/useNetworkStore.js';
import { useAuthStore } from '/src/store/stores/useAuthStore.js';
import { useDeviceStore } from '/src/store/stores/useDeviceStore.js';

import { getDevice } from '/src/apis/device.js';

// router definition
const router = createRouter({
	base: import.meta.env.VITE_APP_URL,
	history: createWebHistory(),
	routes: routes({ authGuard, guestGuard }),
});

export default router;

/**
 * Apply beforeEach guard to the routes.
 *
 * @param  {Function} callback
 */
router.beforeEach(async (to, from, next) => {
	// Access stores INSIDE the guard, not at module level
	const { token, authenticated, setToken, setAuthenticated, isReady: isAuthReady } = useAuthStore();
	const { network } = useNetworkStore();
	const { setDevice, getConfigs, getTypes } = useDeviceStore();

	// Wait for auth store to hydrate from storage
	await isAuthReady();

	if (token.value && !authenticated.value && network.value) {
		try {
			const device = await getDevice();
			setDevice(device);
			setAuthenticated(true);

			getConfigs();
			getTypes();
		} catch (error) {
			if (error?.status === 401) {
				// Token is invalid/expired - clear it
				setToken(undefined);
				setAuthenticated(false);
				return next({ name: 'Login' });
			}

			// Other errors (network, 500, etc.) - don't clear token, just redirect
			return next({ name: 'Login' });
		}
	}

	next();
});

/**
 * Apply beforeEnter guard to the routes.
 *
 * @param  {Array} routes
 * @param  {Function} callback
 * @return {Array}
 */
function beforeEnter (routes, callback) {
	return routes.map(route => {
		return { ...route, beforeEnter: callback };
	});
}

/**
 * Redirect to login if guest.
 *
 * @param  {Array} routes
 * @return {Array}
 */
function authGuard (routes) {
	return beforeEnter(routes, (to, from, next) => {
		const { token } = useAuthStore();
		if (!token.value) {
			return next({ name: 'Login' });
		}
		next();
	});
}

/**
 * Redirect to Home if authenticated.
 *
 * @param  {Array} routes
 * @return {Array}
 */
function guestGuard (routes) {
	return beforeEnter(routes, (to, from, next) => {
		const { token } = useAuthStore();
		if (token.value) {
			return next({ name: 'Home' });
		}

		next();
	});
}
